import { ActionReducerMapBuilder, createAsyncThunk, createSlice, Draft } from "@reduxjs/toolkit";
import { UNKNOWN } from "@src/components/vGrid/VGrid";
import axios, { AxiosError, AxiosResponse } from "axios";
import { ApiProvider } from "./ApiProvider";
import { getInitialState, IDataState } from "./helpers";

export const handleError = (error: unknown) => {
	if (error instanceof AxiosError) {
		const data = error.response?.data;

		if (typeof data === "string") return data;
		if (data?.Message) return data.Message;

		return error.message;
	}

	if (error instanceof Error) return error.message;

	return "Unknown error";
};

type WithSkipCache<T> = T & { skipCache?: boolean; skipSavingCache?: boolean; cacheKey?: string };
type IExtraReducers<TState> = (builder: ActionReducerMapBuilder<IDataState<TState>>) => void;
export const getThunkAndSlice = <TParams, TResponse, TState = TResponse>({
	name,
	externalPath,
	path,
	initialState,
	cacheId,
	responsePath,
	method = "get",
	onBeforeRequest,
	onAfterRequest,
	onResponseHeaders,
	onData,
	getHeaders,
	onUnauthorized,
}: {
	name?: string;
	externalPath?: (params: TParams) => string;
	path: string | ((params: TParams) => string);
	initialState: TState;
	cacheId?: keyof TParams;
	method?: "post" | "get" | "postWithFile" | "getFile" | "delete" | "postWithFilePdf";
	responsePath?: keyof TResponse;
	onBeforeRequest?: (params: TParams, state: unknown) => TState | undefined;
	onAfterRequest?: (data: TState, params: TParams, state: unknown, dispatch: UNKNOWN) => Promise<TState | undefined>;
	onResponseHeaders?: (headers: AxiosResponse["headers"], data: TResponse) => TResponse;
	onData?: (data: TState) => void;
	getHeaders?: (params: TParams, state: unknown) => Record<string, string | undefined>;
	onUnauthorized?: (params: TParams, state: unknown, dispatch: UNKNOWN) => Promise<void>;
}) => {
	const iState = getInitialState(initialState);

	const getCacheKey = (params: TParams, cacheId?: keyof TParams): string => {
		if (!params || !cacheId) return "";
		return String(params[cacheId]);
	};

	const pathKey = name ?? (typeof path === "function" ? path({} as TParams) : path).replace(/\//g, "_");

	const thunk = createAsyncThunk<TState, WithSkipCache<TParams>>(pathKey, async (params, opts) => {
		const { rejectWithValue, getState, dispatch, signal } = opts;
		try {
			const state = getState();

			const runRequest = async () => {
				const result = onBeforeRequest?.(params, state);
				if (result) return result;

				const pathString = typeof path === "function" ? path(params) : path;
				const cacheKey = params.cacheKey !== undefined ? params.cacheKey : getCacheKey(params, cacheId);

				if (!params.skipCache) {
					const cached = ApiProvider.default.getCache<TState>(pathKey, cacheKey);
					if (cached && !(Array.isArray(cached) && cached.length === 0)) return cached;
				}

				let provider;
				const headers = getHeaders?.(params, state) || {};

				if (externalPath) {
					const pathString = externalPath(params);
					provider = (await axios.get<TResponse>(pathString, { headers, signal })).data;
				} else if (onResponseHeaders && method === "post") {
					provider = ApiProvider.default
						.postWithHeaders<TResponse>(pathString, params || {}, { headers, signal })
						.then((response) => onResponseHeaders(response.headers, response.data));
				} else {
					provider = ApiProvider.default[method]<TResponse>(pathString, params || {}, { headers, signal });
				}

				const response = await provider;
				let data = (responsePath && typeof response == "object" ? response?.[responsePath] : response) as TState;

				const newData = await onAfterRequest?.(data, params, state, dispatch);
				if (newData) data = newData;

				if (!params.skipSavingCache && !(Array.isArray(data) && data.length === 0)) {
					ApiProvider.default.setCache(pathKey, cacheKey, data);
				}

				return data;
			};

			try {
				return await runRequest();
			} catch (error: unknown) {
				if (onUnauthorized && axios.isAxiosError(error) && error.response?.status === 401) {
					try {
						await onUnauthorized(params, state, dispatch);
						return await runRequest();
					} catch (retryError: unknown) {
						return rejectWithValue(handleError(retryError));
					}
				}
				return rejectWithValue(handleError(error));
			}
		} catch (error: unknown) {
			return rejectWithValue(handleError(error));
		}
	});

	const extraReducers: IExtraReducers<TState> = (builder) => {
		builder
			.addCase(thunk.pending, (state) => {
				state.loading = true;
				state.isInitialLoad = false;
			})
			.addCase(thunk.fulfilled, (state, action) => {
				state.loading = false;
				state.version++;
				state.init = true;
				state.data = action.payload as Draft<TState>;
				onData?.(action.payload);
				state.error = null;
			})
			.addCase(thunk.rejected, (state, action) => {
				if (action.meta.aborted) return;

				state.loading = false;
				state.data = iState.data as Draft<TState>;
				const payload = action.payload as UNKNOWN;
				if (action.error?.message) {
					state.error = action.error.message;
				} else if (payload.Message) {
					state.error = payload.Message;
				} else if (typeof payload === "string") {
					state.error = action.payload as string;
				} else {
					state.error = JSON.stringify(action.payload);
				}
			});
	};

	const slice = createSlice({
		name: pathKey,
		initialState: iState,
		reducers: {
			reset: (state) => {
				state.data = iState.data as Draft<TState>;
			},
			update: (state, data) => {
				state.data = data.payload as Draft<TState>;
			},
		},
		extraReducers,
	});

	return {
		thunk,
		slice,
		extraReducers,
		pathKey,
		getCacheKey,
	};
};
